import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import {
  AdminAppVersionService,
  type ApiError,
  type AppPlatform,
  type AppVersionUpdateRequest,
} from '@/shared/api'

// === ВЕРСИИ ПРИЛОЖЕНИЯ (АДМИНКА) ===

// Ключи React Query
const QUERY_KEYS = {
  list: () => ['app-versions', 'list'] as const,
  history: (platform?: AppPlatform) =>
    ['app-versions', 'history', platform] as const,
}

/** Текущие настройки обновления для всех платформ */
export const useAppVersions = () => {
  return useQuery({
    queryKey: QUERY_KEYS.list(),
    queryFn: () => AdminAppVersionService.list(),
    staleTime: 60 * 1000,
  })
}

/** Полное сохранение настроек платформы */
export const useUpdateAppVersion = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      platform,
      request,
    }: {
      platform: AppPlatform
      request: AppVersionUpdateRequest
    }) => AdminAppVersionService.update(platform, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-versions'] })
      toast.success('Настройки обновления сохранены')
    },
    onError: (error: ApiError) => {
      if (!error?.alreadyToasted) {
        toast.error(error.detail || 'Ошибка при сохранении настроек обновления')
      }
    },
  })
}

/** Включение/выключение кампании обновления платформы */
export const useToggleAppVersion = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      platform,
      isActive,
    }: {
      platform: AppPlatform
      isActive: boolean
    }) => AdminAppVersionService.toggle(platform, isActive),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['app-versions'] })
      toast.success(
        variables.isActive
          ? 'Кампания обновления включена'
          : 'Кампания обновления выключена'
      )
    },
    onError: (error: ApiError) => {
      if (!error?.alreadyToasted) {
        toast.error(error.detail || 'Ошибка при изменении статуса кампании')
      }
    },
  })
}

/** История изменений настроек; платформа — необязательный фильтр */
export const useAppVersionHistory = (platform?: AppPlatform) => {
  return useQuery({
    queryKey: QUERY_KEYS.history(platform),
    queryFn: () => AdminAppVersionService.history(platform),
    staleTime: 30 * 1000,
  })
}
